import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { IconBaseProps, IconType } from 'react-icons';
import {
  FiChevronDown,
  FiChevronRight,
  FiCpu,
  FiGrid,
  FiHardDrive,
  FiHeadphones,
  FiMonitor,
  FiTag,
  FiPackage,
  FiPrinter,
  FiShoppingBag,
  FiVideo,
} from 'react-icons/fi';
import { toCategoryHref } from '../../../utils/constants/navigation.constants';
import { useWooCategoryNavigation } from '../../../hooks/useWooCategoryNavigation';

const renderIcon = (Icon: IconType, props?: IconBaseProps) =>
  React.createElement(Icon as unknown as React.FC<IconBaseProps>, props);

const categoryIcons: Record<string, IconType> = {
  Laptop: FiMonitor,
  'Desktop PC': FiCpu,
  Components: FiHardDrive,
  Accessories: FiHeadphones,
  Printers: FiPrinter,
  Projectors: FiMonitor,
  CCTV: FiVideo,
  'POS System': FiGrid,
  Software: FiTag,
  'Used Items': FiShoppingBag,
  OFFERS: FiPackage,
};

interface CategorySidebarProps {
  topOffset?: number;
}

const CategorySidebar: React.FC<CategorySidebarProps> = () => {
  const { pathname } = useLocation();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const menuItems = useWooCategoryNavigation();

  const toggleItem = (label: string) => {
    setOpenCategory((current) => (current === label ? null : label));
  };

  useEffect(() => {
    setOpenCategory(null);
    setHoveredCategory(null);
  }, [pathname]);

  const isActivePath = (href: string): boolean =>
    pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));

  return (
    <aside className="w-full min-w-0 max-w-full shrink-0 self-start">
      <div className="flex h-auto min-w-0 flex-col border border-white/10 bg-[#121212] shadow-xl shadow-black/20">
        <div className="flex min-w-0 items-center gap-3 border-b border-white/10 bg-indigo-500 px-5 py-[1.125rem]">
          <div className="flex h-5 w-5 shrink-0 flex-col justify-center gap-1">
            <span className="block h-0.5 w-full bg-white" />
            <span className="block h-0.5 w-3/4 bg-white" />
            <span className="block h-0.5 w-full bg-white" />
          </div>
          <span className="min-w-0 break-words text-base font-black uppercase tracking-wider text-white">
            All Categories
          </span>
        </div>

        <nav className="flex h-auto min-w-0 flex-col">
          {menuItems.map((item) => {
            const hasChildren = Boolean(item.children?.length);
            const expanded = openCategory === item.label;
            const flyoutVisible = expanded || hoveredCategory === item.label;
            const isParentActive = isActivePath(item.basePath);
            const Icon = categoryIcons[item.label] || FiGrid;
            const childCount = item.children?.length ?? 0;
            const flyoutColumns = childCount > 20 ? 3 : childCount > 10 ? 2 : 1;
            const flyoutWidthClass =
              flyoutColumns === 3 ? 'w-[54rem]' : flyoutColumns === 2 ? 'w-[36rem]' : 'w-72';
            const flyoutGridClass =
              flyoutColumns === 3 ? 'grid-cols-3' : flyoutColumns === 2 ? 'grid-cols-2' : 'grid-cols-1';

            if (!hasChildren) {
              return (
                <Link
                  key={item.label}
                  to={item.basePath}
                  className={`group relative flex min-w-0 items-center justify-between border-b border-l-2 border-b-white/5 text-[1.025rem] font-bold transition-colors duration-150 ${
                    isParentActive
                      ? 'border-l-indigo-400 bg-white/[0.07] text-white'
                      : 'border-l-transparent text-slate-300 hover:border-l-indigo-400 hover:bg-white/[0.04] hover:text-white'
                  }`}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-3 px-5 py-4">
                    {renderIcon(Icon, {
                      size: 20,
                      className: `shrink-0 ${
                        isParentActive ? 'text-indigo-400' : 'text-slate-500 group-hover:text-indigo-300'
                      }`,
                    })}
                    <span className="min-w-0 break-words">{item.label}</span>
                    {item.hotBadge && (
                      <span className="shrink-0 rounded-sm bg-red-500 px-1 py-0.5 text-[9px] font-black leading-none text-white">
                        NEW
                      </span>
                    )}
                  </span>
                  <span className="flex w-12 self-stretch items-center justify-center border-l border-white/10">
                    {renderIcon(FiChevronRight, {
                      size: 15,
                      className: 'shrink-0 text-slate-600 transition-colors group-hover:text-slate-300',
                    })}
                  </span>
                </Link>
              );
            }

            return (
              <div
                key={item.label}
                className="group/category relative min-w-0 border-b border-white/5"
                onMouseEnter={() => setHoveredCategory(item.label)}
                onMouseLeave={() => setHoveredCategory(null)}
              >
                <div
                  className={`group relative flex min-w-0 items-stretch overflow-hidden border-l-2 transition-colors duration-150 ${
                    isParentActive
                      ? 'border-l-indigo-400 bg-white/[0.07] text-white'
                      : 'border-l-transparent text-slate-300 hover:border-l-indigo-400 hover:bg-white/[0.04] hover:text-white'
                  }`}
                >
                  <Link
                    to={item.basePath}
                    className="flex min-w-0 flex-1 items-center gap-3.5 px-5 py-4 text-left text-[1.025rem] font-bold"
                  >
                    {renderIcon(Icon, {
                      size: 20,
                      className: `shrink-0 ${
                        isParentActive ? 'text-indigo-400' : 'text-slate-500 group-hover:text-indigo-300'
                      }`,
                    })}
                    <span className="min-w-0 break-words">{item.label}</span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggleItem(item.label)}
                    className="flex w-12 shrink-0 items-center justify-center border-l border-white/10 transition hover:bg-white/15"
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label} categories`}
                    aria-expanded={expanded}
                    aria-controls={`category-panel-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <span className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
                    {renderIcon(FiChevronDown, {
                      size: 15,
                      className: 'text-slate-500 transition-colors group-hover:text-slate-300',
                    })}
                    </span>
                  </button>
                </div>

                <div
                  id={`category-panel-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`absolute left-full top-0 z-[70] max-w-[calc(100vw-360px)] origin-top-left transition-all duration-200 ease-out ${flyoutWidthClass} ${
                    flyoutVisible
                      ? 'visible translate-x-0 opacity-100'
                      : 'invisible -translate-x-2 opacity-0'
                  }`}
                >
                  <div className={`grid max-h-[calc(100vh-140px)] min-w-0 gap-y-1 overflow-y-auto rounded-r-lg border border-white/10 bg-[#111111]/[0.98] py-2 shadow-2xl shadow-black/60 backdrop-blur-xl [scrollbar-color:#6366f1_#111111] [scrollbar-width:thin] ${flyoutGridClass}`}>
                    {item.children?.map((child) => {
                      const childHref = child.href || toCategoryHref(item.basePath, child.slug);
                      const isChildActive = pathname === childHref;

                      return (
                        <Link
                          key={child.slug}
                          to={childHref}
                          onClick={(event) => {
                            event.currentTarget.blur();
                            setOpenCategory(null);
                            setHoveredCategory(null);
                          }}
                          className={`mx-2 flex min-w-0 items-start gap-2.5 rounded-lg border px-4 py-3 text-[0.9375rem] font-semibold transition-colors duration-150 ${
                            isChildActive
                              ? 'border-indigo-400 bg-indigo-500 text-white shadow-[0_0_12px_rgba(99,102,241,0.35)]'
                              : 'border-transparent text-slate-400 hover:border-white/10 hover:bg-white/[0.06] hover:text-white'
                          }`}
                        >
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-600" />
                          <span className="min-w-0 break-words leading-5">{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default CategorySidebar;
